import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import apiService from '../../services/ApiService';
import SpinnerOverlay from '../helpers/SpinnerOverlay';

const BannerForm = ({ bannerToEdit }) => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [contentImage, setContentImage] = useState(null);
  const [brandId, setBrandId] = useState('');
  const [type, setType] = useState('');
  const [link, setLink] = useState('');
  const [sort, setSort] = useState('');
  const [homeSort, setHomeSort] = useState('');
  const [availableTitleSorts, setAvailableTitleSorts] = useState([]);
  const [availableBodySorts, setAvailableBodySorts] = useState([]);
  const [availableHomeSorts, setAvailableHomeSorts] = useState([]);
  const [visible, setVisible] = useState(null);
  const [loading, setBlogLoading] = useState(false);

  useEffect(() => {
    if (bannerToEdit) {
      setContentImage(bannerToEdit.contentImage);
      // Handle brandId - could be string or object with _id
      const brandIdValue = bannerToEdit.brandId?._id || bannerToEdit.brandId || bannerToEdit.brand?._id || bannerToEdit.brand || '';
      setBrandId(brandIdValue);
      setType(bannerToEdit.type);
      setLink(bannerToEdit.link || '');
      setSort(bannerToEdit.sort?.toString() || '');
      setHomeSort(bannerToEdit.homeSort?.toString() || '');
      setVisible(bannerToEdit.visible);
    }
  }, [bannerToEdit]);

  const getAvailableSortNumbers = async (brandIdParam, bannerType) => {
    try {
      const [success, error] = await apiService.get(`banners/sort?brandId=${brandIdParam}&type=${bannerType}`);
      if (error) throw new Error("Greska pri dohvatanju slobodnih brojeva!");
      if (bannerType === 'title') {
        setAvailableTitleSorts(success.data);
      } else {
        setAvailableBodySorts(success.data);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getAvailableHomeSortNumbers = async () => {
    try {
      const [success, error] = await apiService.get(`banners/home-sort?type=body`);
      if (error) throw new Error("Greska pri dohvatanju slobodnih brojeva!");
      setAvailableHomeSorts(success.data);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadAvailableSorts = useCallback(async () => {
    if (!brandId) return;
    await Promise.all([
      getAvailableSortNumbers(brandId, 'title'),
      getAvailableSortNumbers(brandId, 'body'),
    ]);
  }, [brandId]);

  useEffect(() => {
    loadAvailableSorts();
  }, [loadAvailableSorts]);

  useEffect(() => {
    getAvailableHomeSortNumbers();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // For edit mode, contentImage is optional if not changed
    if (!bannerToEdit && !contentImage) {
      toast.warn('Slika je obavezna!');
      return;
    }

    if (!type || !sort) {
      toast.warn('Tip i sort su obavezni!');
      return;
    }

    if (type === 'body' && !homeSort) {
      toast.warn('Home sort je obavezan za body bannere!');
      return;
    }

    const formData = new FormData();

    // Only append contentImage if it's a File object (new upload) or if editing without existing image
    if (contentImage instanceof File) {
      formData.append("contentImage", contentImage);
    } else if (!bannerToEdit) {
      // For create, image is required
      toast.warn('Slika je obavezna!');
      return;
    }

    formData.append("brandId", brandId);
    formData.append("type", type);
    formData.append("link", link || '');
    formData.append("sort", parseInt(sort));
    if (type === 'body') {
      formData.append("homeSort", parseInt(homeSort));
    }
    formData.append("visible", visible == null ? false : visible);

    if (bannerToEdit) {
      updateBanner(bannerToEdit._id, formData);
    } else {
      createBanner(formData);
    }
  };

  const createBanner = async (newBanner) => {
    setBlogLoading(true);
    const [, error] = await apiService.post("banners", newBanner);
    setBlogLoading(false);

    if (error) {
      toast.error(error.response.data.error.message || "Greška pri čuvanju bannera!");
    } else {
      toast.success("Baner uspesno sacuvan!");
      resetForm();
      navigate('/banner/view');
    }
  };

  const updateBanner = async (id, newBanner) => {
    setBlogLoading(true);
    const [, error] = await apiService.put(`banners/${id}`, newBanner);
    setBlogLoading(false);

    if (error) {
      toast.error(error.response.data.error.message || "Greška pri čuvanju bannera!");
    } else {
      toast.success("Baner uspešno izmenjen!");
      resetForm();
      navigate('/banner/view');
    }
  };

  const resetForm = () => {
    setContentImage(null);
    setBrandId('');
    setType('');
    setLink('');
    setSort('');
    setHomeSort('');
  };

  const handleImageChange = (e) => setContentImage(e.target.files[0]);

  const getBrands = useCallback(async () => {
    const [success, error] = await apiService.get("brands");
    if (error) {
      toast.error("Greska pri preuzimanju brendova!");
    } else {
      setBrands(success.data);
      if (!bannerToEdit && !brandId && success.data[0]) {
        setBrandId(success.data[0]._id);
      }
    }
  }, [bannerToEdit, brandId]);

  useEffect(() => {
    getBrands();
  }, [getBrands]);

  return (
    <div className="container mt-5">
      <h2>{bannerToEdit ? 'Izmeni Banner' : 'Dodaj Banner'}</h2>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="contentImage" className="form-label">Slika</label>
          <input
            type="file"
            className="form-control"
            id="contentImage"
            accept="image/*"
            onChange={handleImageChange}
            required={!bannerToEdit}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="brandId" className="form-label">Brand ID</label>
          <select
            className="form-select"
            id="brandId"
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
          >
            {brands.map((brand) => (
              <option key={brand._id} value={brand._id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="type" className="form-label">Tip Bannera</label>
          <select
            className="form-select"
            id="type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            required
          >
            <option value="" disabled>Odaberite tip</option>
            <option value="title">Header</option>
            <option value="body">Body</option>
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="link" className="form-label">Link</label>
          <input
            type="text"
            className="form-control"
            id="link"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="sort" className="form-label">Sort</label>
          <input
            type="number"
            className="form-control"
            id="sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            min="1"
            max="99"
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="homeSort" className="form-label">Home Sort</label>
          <input
            type="number"
            className="form-control"
            id="homeSort"
            value={homeSort}
            onChange={(e) => setHomeSort(e.target.value)}
            min="1"
            max="99"
            required={type === 'body'}
            disabled={type !== 'body'}
          />
        </div>

        {bannerToEdit && (
          <div className="form-check mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              id="visible"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="visible">Visible</label>
          </div>
        )}

        <button type="submit" className="btn btn-primary">
          {bannerToEdit ? 'Ažuriraj Banner' : 'Dodaj Banner'}
        </button>
      </form>
      <SpinnerOverlay isLoading={loading} />
      <br />
      <p>
        Slobodni title sort brojevi: {availableTitleSorts.join(', ')}
        <br />
        Slobodni body sort brojevi: {availableBodySorts.join(', ')}
        <br />
        Slobodni home sort brojevi: {availableHomeSorts.join(', ')}</p>
    </div>
  );
};

export default BannerForm;
